import { LucideIcon } from 'lucide-react';
import { FontSizeKey } from '../context/FontSizeContext';
import { DateRangeFilterState, FilterState, SortConfig } from './tableFilterSettings';
import { orderTypeLabelMap, orderTypeLabelsByCompany } from '../models/orderLabels';
import { resolveCompanyCode } from '../utils/company';

// Every status a mold can hold in the database. The status filter keys its
// options on this type (statusFilterConfig.ts), so adding a status here without
// a filter entry fails the type check.
export type MoldStatus = 'active'
  | 'repairing'
  | 'modifying'
  | 'in_servicing'
  | 'inactive'
  | 'eol'
  | 'closed'
  | 'arriving'
  | 'pending_export'
  | 'released_to_external';

export interface Mold {
  sbiId: string;
  moldNumber: string;
  /**
   * Real columns, but nullable and company-specific: SK populates them, SBI
   * leaves them NULL, and the backend's omitempty then drops the keys from
   * the response entirely. Optional so a consumer has to handle their
   * absence rather than trusting a string.
   */
  moldCode?: string;
  moldName?: string;
  /** Nullable, like moldCode/moldName above. */
  moldSource?: string;
  makerName?: string;
  /** Written by the create-mold form; not returned by the read endpoints. */
  shotThreshold?: number;
  productName?: string;
  customer: Customer;
  customerId?: number;
  revision: number;
  servicingAlertShotThreshold: number;
  servicingMaxShotThreshold: number;
  shotCount: number;
  cycleShotCount: number;
  machineSizeMin: number;
  machineSizeMax: number;
  cavity: string;
  weight: number;
  width: number;
  height: number;
  length: number;
  plate: string;
  insertEngraving: boolean;
  insertChangeType: string;
  id: string;
  name?: string;
  status: MoldStatus;
  maxShotCount: number;
  productParts: ProductPart[];
  inserts: Insert[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  locatedRingOd: number;
  nozzleSr: number;
  gateType: string;
  hotRunner: boolean;
  hotRunnerType: string;
  hotRunnerBrand: string;
  qtyZoneHr: string;
  delayGate: boolean;
  currentLocation: string;
  storageLocation: string;
  assetNoCustomer: string;
  openMoldOrder?: MoldOrder;
  moldType: string;
  remarks: string;
  physicalShotCount: number;
  extraAttributes?: Record<string, any>;
  /** When the mold was marked end-of-life; stays set while it is used as an emergency spare. */
  eolAt?: string | null;
}

// Define a specific type for mold creation data
export interface MoldCreationData extends Omit<Partial<Mold>, 'productParts'> {
  customerId: number;
  productPartIds: number[];
  insertIds: number[];
}

export interface DisplayMoldOrderStatus {
  id: MoldOrderStatus;
  name: string;
  color: string;
  colorCode: string;
  textColor: string;
  hoverColor: string;
  countBg: string;
  icon: LucideIcon;
  statusIds: MoldOrderStatus[];
}

export interface FilterMoldOrderStatus {
  id: string;
  name: string;
  color: string;
  colorCode: string;
  textColor: string;
  hoverColor: string;
  countBg: string;
  icon: LucideIcon;
  statusIds: MoldOrderStatus[];
}

export enum MoldOrderType {
  // ล้างทั้งระบบ เพื่อเก็บ
  ALL_STORE = 'maintenance.ALL.store',
  // ล้างทั้งระบบ เพื่อผลิต
  ALL_PRODUCTION = 'maintenance.ALL.production',
  // ล้างเพื่อผลิต
  WAITING = 'maintenance.WAITING',
  // ล้างเพื่อเก็บ ครบ Order
  STORE_ORDER_COMPLETE = 'maintenance.STORE.order_complete',
  // ล้างเพื่อเก็บ ปิด​ Mold
  STORE_CLOSE_MOLD = 'maintenance.STORE.close_mold',
  // ล้างเพื่อเก็บ EOL
  STORE_EOL = 'maintenance.STORE.eol',
  // ล้างเพื่อทดสอบ
  MAINTENANCE_TESTING = 'maintenance.TESTING',

  /*** For repair ***/
  // ซ่อมหน้าเครื่อง
  REPAIR_MINOR = 'repair.MINOR',
  // ซ่อมทั่วไป
  REPAIR_MAJOR = 'repair.MAJOR',
  // แก้ไขแม่พิมพ์
  MODIFY = 'modify',

  /*** For mold acceptance ***/
  MOLD_ACCEPTANCE = 'mold_acceptance',

  /*** SK: maintenance ***/
  MAINTENANCE_LEVEL1 = 'maintenance.LEVEL1',
  MAINTENANCE_LEVEL2 = 'maintenance.LEVEL2',
  REPAIR = 'repair',

  /*** For mold export */
  MOLD_EXPORT = 'mold_export',
}

export type MoldOrderFirstLevelType = 'repair' | 'maintenance' | 'mold_acceptance' | 'modify' | 'mold_export';

/** Resolve a persisted exact order type to its create/workflow first-level group.
 *  Pure standalone helper so navigation code need not reach into React context. */
export const getMoldOrderFirstLevelType = (type: MoldOrderType | MoldOrderMainType): MoldOrderFirstLevelType => {
  if (type.startsWith('maintenance')) return 'maintenance';
  if (type.startsWith('repair')) return 'repair';
  if (type === 'modify') return 'modify';
  if (type.startsWith('mold_acceptance')) return 'mold_acceptance';
  if (type === 'mold_export') return 'mold_export';
  throw new Error(`Unknown MoldOrderType: ${type}`);
};

/** How much of a source order a follow-up carries forward.
 *  - none: a blank order.
 *  - metadata: order-CREATION inputs only (note/priority/…); questionnaire re-filled.
 *  - all: creation inputs + the version-diffed questionnaire answers + photos. */
export type CopyMode = 'none' | 'metadata' | 'all';

// First-level order-type (order_group) options for the admin pickers — the form-json editor
// and the order export. One shared source so the bilingual labels read identically across
// both screens: relabel a type here and every admin picker follows.
export const firstLevelTypeOptions: {
  value: MoldOrderFirstLevelType;
  label: string;
}[] = [
  { value: 'maintenance', label: 'Maintenance (ล้างแม่พิมพ์)' },
  { value: 'repair', label: 'Repair (ซ่อม)' },
  { value: 'modify', label: 'Modify (แก้ไข)' },
  { value: 'mold_acceptance', label: 'Mold Acceptance (รับแม่พิมพ์)' },
  { value: 'mold_export', label: 'Mold Export (ส่งออกแม่พิมพ์)' },
];

export enum MoldOrderMainType {
  ALL_OVERHAUL = 'ALL_OVERHAUL',
  PRODUCTION = 'PRODUCTION',
  STORE = 'STORE',
  MOLD_ACCEPTANCE = 'MOLD_ACCEPTANCE',
  TESTING = 'TESTING',

  MAINTENANCE_LEVEL1 = 'maintenance.LEVEL1',
  MAINTENANCE_LEVEL2 = 'maintenance.LEVEL2',

  REPAIR_MINOR = 'repair.MINOR',
  REPAIR_MAJOR = 'repair.MAJOR',
  MODIFY = 'modify',
  REPAIR = 'repair',
}

// label comes from the shared order-export source (orderTypeLabelMap); order + cardStyle stay here.
const withOrderTypeLabel = <T extends { value: MoldOrderType }>(items: T[]) =>
  items.map((t) => ({ ...t, label: orderTypeLabelMap[t.value] ?? '' }));

export const moldOrderTypesForMaintenance = withOrderTypeLabel([
  { value: MoldOrderType.ALL_STORE, cardStyle: 'bg-blue-100 text-blue-800' },
  { value: MoldOrderType.ALL_PRODUCTION, cardStyle: 'bg-teal-100 text-teal-800' },
  { value: MoldOrderType.WAITING, cardStyle: 'bg-violet-100 text-violet-800' },
  { value: MoldOrderType.STORE_ORDER_COMPLETE, cardStyle: 'bg-amber-100 text-amber-800' },
  { value: MoldOrderType.STORE_CLOSE_MOLD, cardStyle: 'bg-orange-100 text-orange-800' },
  { value: MoldOrderType.STORE_EOL, cardStyle: 'bg-fuchsia-100 text-fuchsia-800' },
  { value: MoldOrderType.MAINTENANCE_TESTING, cardStyle: 'bg-green-100 text-green-800' },
])

export const moldOrderTypes = [
  ...moldOrderTypesForMaintenance,
  ...withOrderTypeLabel([
    { value: MoldOrderType.MOLD_ACCEPTANCE, cardStyle: 'bg-green-100 text-green-800' },
    { value: MoldOrderType.MAINTENANCE_LEVEL1, cardStyle: 'bg-blue-100 text-blue-800' },
    { value: MoldOrderType.MAINTENANCE_LEVEL2, cardStyle: 'bg-teal-100 text-teal-800' },
    { value: MoldOrderType.REPAIR_MINOR, cardStyle: 'bg-blue-100 text-blue-800' },
    { value: MoldOrderType.REPAIR_MAJOR, cardStyle: 'bg-teal-100 text-teal-800' },
    { value: MoldOrderType.MODIFY, cardStyle: 'bg-indigo-100 text-indigo-800' },
    { value: MoldOrderType.REPAIR, cardStyle: 'bg-indigo-100 text-indigo-800' },
    { value: MoldOrderType.MOLD_EXPORT, cardStyle: 'bg-indigo-100 text-indigo-800' },
  ]),
];

// This (create-repair) picker list intentionally keeps its OWN labels rather than the shared
// orderTypeLabelMap: its REPAIR_MAJOR reads "ซ่อมทั่วไป" (no "(Repair)" suffix), which differs from
// the canonical getOrderTypeLabel value "ซ่อมทั่วไป (Repair)". So it is NOT routed through
// withOrderTypeLabel — folding it in would change this list's displayed label.
export const moldOrderTypesForRepair = [
  {
    value: MoldOrderType.REPAIR_MINOR,
    label: 'ซ่อมหน้าเครื่อง',
    cardStyle: 'bg-blue-100 text-blue-800'
  },
  {
    value: MoldOrderType.REPAIR_MAJOR,
    label: 'ซ่อมทั่วไป',
    cardStyle: 'bg-teal-100 text-teal-800'
  },
  {
    value: MoldOrderType.MODIFY,
    label: 'Modify แม่พิมพ์',
    cardStyle: 'bg-indigo-100 text-indigo-800'
  },
]

export interface MoldOrderMainTypeStruct {
  value: MoldOrderMainType;
  label: string;
  subTypes: MoldOrderType[];
}

export const moldOrderMainTypes: MoldOrderMainTypeStruct[] = [
  {
    value: MoldOrderMainType.ALL_OVERHAUL,
    label: 'ล้างทั้งระบบ',
    subTypes: [
      MoldOrderType.ALL_STORE,
      MoldOrderType.ALL_PRODUCTION,
    ]
  },
  {
    value: MoldOrderMainType.PRODUCTION,
    label: 'ล้างเพื่อผลิต',
    subTypes: [
      MoldOrderType.WAITING,
    ]
  },
  {
    value: MoldOrderMainType.STORE,
    label: 'ล้างเพื่อเก็บ',
    subTypes: [
      MoldOrderType.STORE_ORDER_COMPLETE,
      MoldOrderType.STORE_CLOSE_MOLD,
      MoldOrderType.STORE_EOL,
    ]
  },
  // {
  //   value: MoldOrderMainType.TESTING,
  //   label: 'ล้างเพื่อทดสอบ',
  //   subTypes: [
  //     MoldOrderType.MAINTENANCE_TESTING
  //   ]
  // },
]

export const isStoreOrderType = (type: MoldOrderType) => {
  return type.startsWith('maintenance.STORE');
}

export const isCleanAllOrderType = (type: MoldOrderType) => {
  return type.startsWith('maintenance.ALL');
}

export const requireStorageLocation = (type: MoldOrderType) => {
  return isStoreOrderType(type) || [MoldOrderType.ALL_STORE].includes(type);
}

export const requireShiftCompletedInfo = (type: MoldOrderType) => {
  return [MoldOrderType.ALL_PRODUCTION, MoldOrderType.WAITING].includes(type);
};

/**
 * The Thai label for an order type, honouring the deploy company's override.
 *
 * A shared type can carry a different shop-floor name per tenant (SK's ซ่อมเล็ก vs SBI's
 * ซ่อมหน้าเครื่อง for repair.MINOR), so the company-scoped map wins over the shared one.
 * `company` defaults to the deploy's own code, which is what every current caller wants —
 * a deploy serves one tenant — so callers only pass it when rendering another tenant's data.
 *
 * TO BE DEPRECATED: move to configuration
 */
export const getOrderTypeLabel = (
  type: MoldOrderType,
  company: string = resolveCompanyCode(),
) => {
  const override = orderTypeLabelsByCompany[company]?.[type];
  if (override) return override;
  return moldOrderTypes.find((t) => t.value === type)?.label || '';
};

export const getOrderTypeCardStyle = (type: MoldOrderType) => {
  return moldOrderTypes.find((t) => t.value === type)?.cardStyle || '';
};

export const getMainTypeLabelFromSubType = (subType: MoldOrderType) => {
  const mainType = moldOrderMainTypes.find((mainType) => isInMoldOrderMainType(mainType.value, subType));
  return mainType?.label || '';
};

export const getMainTypeFromSubType = (subType: MoldOrderType) => {
  if (isStoreOrderType(subType)) {
    return MoldOrderMainType.STORE;
  } if (isCleanAllOrderType(subType)) {
    return MoldOrderMainType.ALL_OVERHAUL;
  } if (subType === MoldOrderType.MOLD_ACCEPTANCE) {
    return MoldOrderMainType.MOLD_ACCEPTANCE;
  }
  return MoldOrderMainType.PRODUCTION;
}

export const isInMoldOrderMainType = (type: MoldOrderMainType, subType: MoldOrderType): boolean => {
  const mainType = moldOrderMainTypes.find((t) => t.value === type);
  return mainType?.subTypes.includes(subType) || false;
};

export const getOrderMainTypeLabel = (type: MoldOrderMainType) => {
  return moldOrderMainTypes.find((t) => t.value === type)?.label || '';
};

export interface FieldFormatter<T = any> {
  format: (value: T, options?: T[]) => string | undefined;
  validate?: (value: T) => boolean;
}

export type FormatterMap = {
  [K in FieldType]: FieldFormatter;
};

/**
 * Base config for all form fields.
 *
 * @property required - Whether the field is required. Can be a boolean or a function that receives the current form values and returns a boolean.
 *   Example: required: true | ((values: T) => boolean)
 */
export interface BaseFieldConfig<T = any> {
  key: keyof T; // Ensure field.key is always a valid key of the provided type T
  label: string;
  type: FieldType;
  /**
   * Whether the field is required. Can be a boolean or a function that receives the current form values and returns a boolean.
   * Example: required: true | ((values: T) => boolean)
   */
  required: boolean | ((values: T) => boolean);
  placeholder?: string;
  showIf?: (values: T) => boolean;
  disabled?: boolean;
  /**
   * Roles that may still edit the field when `disabled` is true.
   * Example: { disabled: true, enabledForRoles: ['ADMIN'] } renders read-only
   * for everyone except admins.
   */
  enabledForRoles?: string[];
  onSaveCustomValue?: (value: string) => void; // Function to save custom value for select_with_other_and_custom
  defaultValue?: string | number; // Default value for the field
  defaultValueFromMold?: (mold: Mold) => string | number | undefined; // Default value derived from the selected mold
  entityToUpdate?: 'moldOrder' | 'orderDetail';
}

export type FieldType =
  'select'
  | 'date'
  | 'text'
  | 'textarea'
  | 'number'
  | 'select_with_other'
  | 'select_with_other_and_custom'
  | 'search_dropdown'
  | 'multi_selector'
  | 'button_options'
  | 'boolean'
  | 'toggle'
  | 'computed';

export interface SelectFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'select';
  options: Array<Option>;
  fetch?: () => Promise<Option[]>;
}

export interface ButtonOption {
  value: string | number;
  label: string;
  group?: string;
  icon?: LucideIcon;
  borderColor?: string;
  textColor?: string;
  hoverColor?: string;
  bgColor?: string;
  selectedBgColor?: string;
  isCustom?: boolean; // Flag to mark custom user-entered values
}

export interface ButtonOptionsFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'button_options';
  options: Array<ButtonOption>;
  fetch?: () => Promise<Option[]>;
}

export interface SelectWithOtherFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'select_with_other';
  options: Array<Option>;
  displayRawValue?: boolean;
  fetch?: () => Promise<Option[]>;
}

export interface SelectWithOtherCustomFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'select_with_other_and_custom';
  options: Array<Option>;
  displayRawValue?: boolean;
  fetch?: () => Promise<Option[]>;
  onSaveCustomValue?: (value: string) => void; // Function to save custom value
}

export interface Option {
  value: string | number;
  label: string;
}

export interface MultiSelectorFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'multi_selector';
  options: Array<Option>;
  onSelect?: (selected: string[]) => void;
  allowOther?: boolean; // Enable adding custom values via dedicated button
  onSaveCustomValue?: (value: string) => void; // Callback to save custom values
  customValuePlaceholder?: string; // Placeholder for custom input field
}

// Dependency source types for computed fields
export type DependencySource = 'form' | 'props' | 'moldOrder';

// Enhanced dependency configuration
export interface DependencyConfig {
  // Source of the dependency (form, props, moldOrder, etc.)
  source?: DependencySource;
  // Field path - can be nested using dot notation (e.g., 'form.repairAssessment', 'moldOrder.status')
  path: string;
  // Optional transformation function name to apply to the value
  transform?: string;
}

// Backward compatible dependency fields (simple string array)
export type SimpleDependencyFields<T> = Array<keyof T>;

// Enhanced dependency fields (array of dependency configurations)
export type EnhancedDependencyFields = DependencyConfig[];

export interface ComputedFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'computed';
  computedConfig: {
    id: string;
    // Support both old format (simple strings) and new format (dependency configs)
    dependencyFields?: SimpleDependencyFields<T> | EnhancedDependencyFields;
  };
}

export interface SearchDropdownFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'search_dropdown';
  options: Array<Option>;
  displayRawValue?: boolean;
  fetch?: () => Promise<Option[]>;
  enforceDropdownSelection: boolean;
  validationMessage?: string;
  onInvalidInput?: (invalidValue: string) => void;
  allowInvalidInput?: boolean;
  // New properties for creating options
  allowCreateNewOption?: boolean;
  createOptionConfig?: {
    entityType: string; // Maps to fetchersRegistry key
    onCreateSuccess?: (newOption: Option) => void;
  };
}

export interface TextFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'text';
  maxLength?: number;
}

export interface TextareaFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'textarea';
}

export interface BooleanFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'boolean';
}

export interface ToggleFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'toggle';
  toggleLabel?: {
    on: string;
    off: string;
  }
}

export interface NumberFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'number';
  min?: number;
  max?: number;
}

export interface DateFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: 'date';
  minDate?: Date;
  maxDate?: Date;
  defaultDateOffset?: number; // Offset in days from today
}

export type FormFieldConfig<T = any> =
  | SelectFieldConfig<T>
  | SelectWithOtherFieldConfig<T>
  | SelectWithOtherCustomFieldConfig<T>
  | SearchDropdownFieldConfig<T>
  | ButtonOptionsFieldConfig<T>
  | TextFieldConfig<T>
  | TextareaFieldConfig<T>
  | NumberFieldConfig<T>
  | DateFieldConfig<T>
  | BooleanFieldConfig<T>
  | ToggleFieldConfig<T>
  | MultiSelectorFieldConfig<T>
  | ComputedFieldConfig<T>;

export type ShiftType = 'A' | 'B';

export interface OrderDetail {
  dueDate?: Date;
  shiftCompleted?: ShiftType;
  moldOrderType: MoldOrderType;
  moldOrderSubType?: string;
  storageLocation?: string;
  customStorageLocation?: string;
  accumulativeShotCount?: number;
  note?: string;
  customNote?: string;
  form: Record<string, any>;
  formVersion?: string;
  machineToProduce?: string;
  parentOrderId?: number;
  rejectPhotoTag?: string;

  // New fields for mold acceptance
  expectedArrivalDate?: Date;
  arrivalDate?: Date;

  // New fields for repair
  problemDuring?: string;
  repairActions?: string[];

  priority?: number;
  rootCause?: string;
  modifyCause?: string;

  // New field for modify
  modifyRequirementNote?: string;

  // New field for mold export
  expectedExportDate?: Date;
  exportDate?: Date;
}

export type OrderDetailKey = keyof OrderDetail;

export type ActionType = 'accept' | 'reject' | 'rework';

export enum MoldOrderShownInTimelineAsStatus {
  PAST = 'past',
  CURRENT = 'current',
  FUTURE = 'future',
};

export enum MoldOrderStatus {
  DRAFT = 'draft',
  CREATED = 'created',
  PENDING_ACKNOWLEDGE = 'pending_acknowledge',
  PICK_UP = 'pick_up',
  IN_PROGRESS = 'in_progress',
  SUPERVISOR_REVIEW = 'supervisor_review',
  REPORTER_CONFIRM = 'reporter_confirm',
  DONE = 'done',
  END_REJECTED = 'rejected',
  COMPLETED = 'completed',
  // Non-existing status is only used in frontend for UI purpose
  NON_EXISTING = 'non_existing',

  // For mold acceptance
  PENDING_ARRIVAL = 'pending_arrival',
  INITIAL_INSPECTION = 'initial_inspection',
  CLEANING_BEFORE_TESTING = 'cleaning_before_testing',
  PENDING_TESTING = 'pending_testing',
  DETAILED_INSPECTION = 'detailed_inspection',
  APPROVAL = 'approval',

  // For repair
  REPORTER_FILL_FORM = 'reporter_fill_form',
  ASSESSMENT = 'assessment',
  PE_ASSESSMENT_APPROVAL = 'pe_assessment_approval',
  MARKETING_APPROVAL = 'marketing_approval',
  PLANNING_APPROVAL = 'planning_approval',
  PENDING_REPAIR = 'pending_repair',
  REPAIR_IN_PROGRESS = 'repair_in_progress',
  REPORTER_REVIEW = 'reporter_review',
  PE_RESULT_APPROVAL = 'pe_result_approval',

  // For modify
  MODIFICATION_APPROVAL = 'modification_approval',
  CUSTOMER_REVIEW = 'customer_review',

  // For mold export
  PENDING_INSPECTION = 'pending_inspection',
  PE_APPROVAL = 'pe_approval',
  GM_APPROVAL = 'gm_approval',
  PENDING_EXPORT = 'pending_export'
}

export interface StateInstance {
  id: number;
  moldOrderId: number;
  status: MoldOrderStatus;
  assignedTo: {
    createdAt: string;
    id: number;
    isActive: boolean;
    roleId: number;
    updatedAt: string;
    username: string;
    fullname: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
  endTime?: string;
  startTime?: string;
  approverSlots: ApproverSlot[];
}

export enum TransitionActionType {
  ACCEPT = 'accept',
  REJECT = 'reject',
  REWORK = 'rework'
}

export interface TransitionAction {
  resetsCycleShotCount?: boolean;
  action: TransitionActionType;
  hasConditionalBranches: boolean;
  isAllowed: boolean;
}

export interface ApproverSlot {
  id: number;
  actualApproverUserId: number | null;
  actualApproverUser?: User | null;
  approverType: 'user' | 'role';
  assignedUser?: User;
  assignedRole?: {
    id: number;
    role: string;
    defaultUserId: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
  status: 'pending' | 'approved' | 'rejected';
  approvedAt: string | null;
  comments: string | null;
  userId: number | null;
  roleId: number | null;
  createdAt: string;
}

export interface ApprovalInfo {
  approvalRequired: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approverSlots: ApproverSlot[];
  assignedSlotId: number;
  stateInstanceId: number;
}

export interface MoldOrder {
  id: number;
  type: MoldOrderType;
  status: MoldOrderStatus;
  priority: number;
  orderDetail: OrderDetail;
  createdBy: User;
  assignedTo: User;
  orderTemplate: Record<string, any>;
  // FK to the bound published form-definition revision (order_templates.id).
  // Absent / 0 for legacy (NULL) orders, which keep rendering from bundled JSON.
  // The backend emits this nested as orderTemplate.id; loaders read either shape.
  orderTemplateId?: number;
  createdAt: string;
  updatedAt: string;
  orderGroupId: number;
  parentOrderId: number;
  mold: Mold;
  stateInstances: Array<StateInstance>;
  nextAssignableUsers: Array<UserWithRole>;
  currentAssignableUsers: Array<UserWithRole>;
  plannedWorkingDate: string;
  plannedCompletionDate: string;
  orderWorkflowDefinitionId: number;
  orderWorkflowDefinition: OrderWorkflowDefinition | null;
  workflowStructure: WorkflowStructureElement[] | null;
  transitionActions: TransitionAction[];
  currentStateApproval: ApprovalInfo;

  isSubOrder: boolean;
  hasSubOrders: boolean;
  subOrders?: MoldOrder[];

  // Server-computed derived columns (backend internal/server/derivedfields; api_spec.yml
  // MoldOrderDerivedFields). Absent when the order has no matching state instances. Prefer
  // these over re-deriving from stateInstances — they are the same values the CSV export
  // emits, so screen and export stay in lockstep.
  derived?: MoldOrderDerivedFields;
}

export interface MoldOrderDerivedFields {
  /** startTime of the earliest in_progress state instance (ISO date-time). */
  operationDate?: string;
  /** endTime of the earliest supervisor_review state instance (ISO date-time). */
  supervisorReviewDate?: string;
  /** endTime of the earliest reporter_confirm state instance (ISO date-time). */
  reporterConfirmationDate?: string;
  /** Whole minutes from startTime to endTime of the earliest in_progress state instance. */
  operationDurationMinutes?: number;
}

export interface OrderWorkflowDefinition {
  id: number;
  name: string;
  version: number;
  definition: WorkflowDefinition;
}

export interface WorkflowDefinition {
  labels: Record<string, any>;
  states: StateDefinition[];
}

export interface StateDefinition {
  name: string;
  transitions: StateTransitionDefinition[];
  assignableTo: AssignableTo[];
  isFinal: boolean;
}

export interface StateTransitionDefinition {
  action: string;
  toState: string;
}

export interface AssignableTo {
  role: string;
}

export interface WorkflowStructureElement {
  statusName: string;
  stateInstanceId: number;
  shownInTimelineAs: 'past' | 'current' | 'future';
}

export interface Task {
  id: string;
  moldId: string;
  status: 'created' | 'in_progress' | 'cleaning' | 'pending' | 'completed';
  description: string;
  createdBy: string;
  dueDate: string;
  approved: boolean;
  currentAssignee: TaskAssignee;
  jobNumber: string;
  reportDate: string;
  storedLocation: string;
  shift: string;
  taskType: string;
  auditLogs: AuditLogs;
}

export interface TaskAssignee {
  id: string;
  name: string;
  img: string;
}

export interface AuditLogs {
  new: AuditLog | null;
  inProgress: AuditLog | null;
  cleaning: AuditLog | null;
  pending: AuditLog | null;
  completed: AuditLog | null;
}

export interface AuditLog {
  assignee: TaskAssignee;
  timestamp: string;
}

export interface MoldWithOrders extends Mold {
  moldOrders: MoldOrder[];
}

export interface Customer {
  id: number;
  name: string;
  code: string;
  color: (string | null);
  isActive: boolean;
}

export interface ProductPart {
  id: number;
  partNumber: string;
  partName: string;
  customer: Customer;
  history: string;
  model: string;
  description: string;
  status: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Assignee {
  id: number;
  username: string;
  isActive: boolean;
  roleId: number;
  createdAt: string;
  updatedAt: string;
  //TODO: Remove this
  img?: string;
  name?: string;
}

export interface OrderTemplate {
  id: number;
  name: string;
  revision: number;
  type: string;
  schema: Record<string, any>;
}

export interface File {
  id: number;
  fileName: string;
  fileSize: number;
  uploadedBy: number;
  uploadedAt: string;
  previewUrl: string;
}

export type EntityType = 'mold' | 'mold_order' | 'insert';

// Canonical change_logs entity_type values. Mirrors the backend
// models.ChangeLogType* constants (see api_spec.yml EntityType enum). Kept
// separate from the file/form `EntityType` above so widening the audit
// vocabulary does not change the semantics of file/question associations.
export type ChangeLogEntityType =
  | 'mold'
  | 'mold_revision'
  | 'mold_order'
  | 'mold_order_state_instance'
  | 'state_instance_approver'
  | 'mold_product_part'
  | 'customer'
  | 'product_part'
  | 'file_metadata'
  | 'role'
  | 'insert'
  | 'mold_insert'
  | 'injection_machine'
  | 'selection_config';

export type ChangeType = 'create' | 'update' | 'delete';

export interface ChangeLog {
  id: number;
  entityType: ChangeLogEntityType;
  entityId: number;
  changedBy: Assignee;
  changeTime: string;
  changeType: ChangeType;
  changeDetails: Record<string, ChangeRecord>;
}

// change_details is { "<field>": { old, new } }. Values are JSON-shaped and may
// be strings, numbers, booleans, null, arrays or nested objects, so they are
// typed as `unknown` and formatted at render time (see EventLog.formatValue).
export interface ChangeRecord {
  old: unknown;
  new: unknown;
}

export interface ChangeLogListResponse {
  items: ChangeLog[];
  total: number;
}

export interface UploadFileStatus {
  fileName: string;
  originalFileName?: string;
  error?: string;
}


export interface User {
  id: number;
  username: string;
  fullname: string;
  isActive: boolean;
  roleId: number;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  id: number;
  permission: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: number;
  role: string;
  permissions: Permission[];
  isActive: boolean;
  defaultUserId: number;
  defaultUser: User;
  createdAt: string;
  updatedAt: string;
}

export interface UserWithRole extends User {
  role: Role;
}

// Assignable-permission catalog served by GET /role-permissions/catalog. The
// editor renders checkboxes from this (never a hard-coded frontend list), so a
// new backend permission constant automatically appears in the UI.
export interface PermissionCatalogItem {
  name: string;
  label: string;
  description: string;
}

export interface PermissionCatalogGroup {
  resource: string;
  label: string;
  permissions: PermissionCatalogItem[];
}

export type TripleViewMode = 'kanban' | 'table' | 'calendar';

export interface TableFilterSetting {
  sortConfig: SortConfig;
  filterState: FilterState;
  dateRangeFilters: DateRangeFilterState;
  activeFilter: string | null;
}

export type MoldDetailTab =
  | 'parts'
  | 'tasks'
  | 'events'
  | 'inserts'
  | 'images'
  | 'attachments'
  | 'guides';

export interface UserPreference {
  moldListView: 'card' | 'table';
  moldOrderListView: 'card' | 'table';
  kanbanBoardView: TripleViewMode;
  kanbanBoardType?: string;
  fontScale: FontSizeKey;
  selectedOrderType?: MoldOrderMainType | 'ALL';
  moldOrderTableVisibleColumns?: Record<string, boolean>;
  stateActivityTableVisibleColumns?: Record<string, boolean>;
  moldTableVisibleColumns?: Record<string, boolean>;
  tableFilterSettings?: Record<string, TableFilterSetting>;
  moldDetailTab: MoldDetailTab;
  moldPage?: {
    moldOrderTableVisibleColumns?: Record<string, boolean>;
    moldOrderListView: 'card' | 'table';
  }
}

export interface LocationStorage {
  id: number;
  type: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface InputInsert {
  id?: number;
  name: string;
  status: InsertStatus;
  quantity: number;
}

export type InsertStatus = 'active' | 'in_used' | 'cancelled';

export interface Insert {
  id: number;
  name: string;
  status: InsertStatus;
  quantity: number;
  createdAt: string;
  updatedAt: string;
  molds?: Partial<Mold[]>;
}

export interface SelectionConfig {
  id: string; // UUID v7
  seq: number; // Deprecated
  value: string;
}

export interface SelectionConfigItem {
  id: number;
  name: string;
  values: SelectionConfig[];
  createdAt: string;
  updatedAt: string;
}

export interface InjectionMachine {
  id: number;
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  location: string;
  tonnage: number;
  screwDiameter: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ShotCountUpdateLog {
  reportDate: string;
  moldId: string;
  moldSbiId: string;
  moldName: string;
  currentShotCountTotal: number;
  reportShotCountTotal: number;
  reportShotCountGood: number;
  reportShotCountBad: number;
  resultShotCountTotal: number;
  updatedAt: string;
}

export interface FanucReportImportRun {
  id: number;
  reportDate: string;
  type: string;
  fileName: string;
  filePath: string;
  status: string;
  totalGroups: number;
  importedGroups: number;
  reviewGroups: number;
  ignoredGroups: number;
  totalImportedShots: number;
  startedAt?: string;
  finishedAt?: string;
  errorMessage?: string;
}

export interface FanucReportImportItem {
  id: number;
  runId: number;
  reportDate: string;
  machineName?: string;
  moldFileName?: string;
  parsedSbiId?: string;
  parsedMoldId?: number;
  candidateMoldCode?: string;
  sourceRowCount: number;
  amountShot: number;
  goodShot: number;
  badShot: number;
  result: string;
  reasonCode: string;
  matchedMoldId?: number;
  matchedMoldSbiId?: string;
  matchedMoldNumber?: string;
  matchedMoldName?: string;
  matchedMoldCode?: string;
  adminNote?: string;
  resolvedBy?: number;
  resolvedAt?: string;
  createdAt?: string;
}

export interface SearchFieldConfig {
  value: string;
  field: string;
  fieldConfig: { id: string; label: string; placeholder: string }[];
}

/**
 * MoldOrderWithMold represents a lightweight mold order payload returned by the "my works" API.
 * It mirrors the backend `MoldOrderWithMold` shape and is used for both approval and assigned lists.
 */
export interface MoldOrderWithMold {
  id: number;
  type: string;
  status: string;
  priority: number;
  orderDetail: Record<string, any>;
  description: string;
  createdBy: User;
  assignedTo: User;
  createdAt: string;
  updatedAt: string;
  orderGroupId: number;
  parentOrderId: number;
  parentOrder: MoldOrder;
  mold: Mold;
  moldId: number;
  orderTemplate: OrderTemplate;
  orderWorkflowDefinition: OrderWorkflowDefinition | null;
  orderWorkflowDefinitionId: number;
  plannedWorkingDate: string;
  plannedCompletionDate: string;
  hasSubOrders: boolean;
  isSubOrder: boolean;
  subOrders: MoldOrder[];
  workflowStructure: WorkflowStructureElement[];
  completedAt: string | null;
}

export type ApprovalOrder = MoldOrderWithMold;
export type AssignedOrder = MoldOrderWithMold;

export interface MyTasks {
  approvalOrders: ApprovalOrder[];
  assignedOrders: AssignedOrder[];
}
